import Cookies from 'js-cookie';
import MicroModal from 'micromodal';

const {registerModule} = window;

class Modal {
  constructor(element) {
    this.element = element;
    this.initialize();
    // Set modal to be ready after initalization is complete. This can be detected from outside the class to determine if this modal is ready to be interacted with.
    this.modalIsReady = true;
    // Once all the setup is done dispatch an event to the document so other modules know the modal is ready
    this.dispatchModalReady();
  }

  dispatchModalReady() {
    // Adds a new event to document that fires when the modal is ready and can be listened to with addEventListener('everlaw-modal-ready', ...)
    document.dispatchEvent(
      new CustomEvent('everlaw-modal-ready', {detail: {modal: this}}),
    );
  }

  bindUI() {
    this.ui = {};
    this.ui.$modal = this.element.querySelector('.modal');
  }

  isModalOpen() {
    // Micromodal
    return this.ui.$modal.classList.contains(this.openClassName);
  }

  setUrlHashVisible(visible) {
    if (visible) {
      // Modify the URL to include the #hash without changing history
      return history.replaceState(null, '', this.modalIdWithLeadingHash);
    }
    // Modify the URL to remove the modal's ID (ex: #hash), but make sure to keep query params in the URL.
    return history.replaceState(null, '', location.pathname + location.search);
  }

  showModal() {
    if ('mountedModules' in window && Array.isArray(window.mountedModules)) {
      window.mountedModules.forEach((module) => {
        if (
          module instanceof Modal &&
          module !== this &&
          module.microModalTargetId !== this.microModalTargetId
        ) {
          // Close all modals that are not the modal about to be shown, and check to ensure the
          // modal target ID is not the same in case duplicate triggers are used on a page
          module.closeModal();
        }
      });
    }
    if (this.gateModal && Cookies.get('gatec-' + this.modalId) === 'off') {
      // If this is a gated modal and the gate cookie is set then don't show the modal
      return;
    }
    // Modal must be closed (this helps prevent errors if showModal is called multiple times), but don't show the modal if there's a cookie set
    if (!this.isModalOpen() && !this.modalHasExistingCookie()) {
      // Config needs to be passed to show like this or won't apply
      MicroModal.show(this.microModalTargetId, {
        awaitCloseAnimation: true,
        // Prevent the user from scrolling on the page while the page is loaded
        disableScroll: true,
        // Don't autofocus on the first elements (fixes a bug where buttons will show their hover focus state creating different styles)
        disableFocus: true,
        openClass: this.openClassName,
        onShow: (modal) => {
          if (this.enableUrlTrigger) {
            this.setUrlHashVisible(true);
          }
          // Don't set a cookie if cookie mode is disabled
          if (this.areCookiesEnabled()) {
            // Cookie is set after the modal is shown to allow it to show once
            this.setModalCookieIfUnset();
          }
          // onModalOpen can be provided to execute additional code on open, designed mainly for use for non-modal modules, but may be null if not defined
          if (this.onModalOpen) {
            this.onModalOpen(modal);
          }
        },
        onClose: (modal) => {
          if (this.enableUrlTrigger) {
            this.setUrlHashVisible(false);
          }
          // onModalClose can be provided to execute additional code on open, designed mainly for use for non-modal modules, but may be null if not defined
          if (this.onModalClose) {
            this.onModalClose(modal);
          }

          const iframe = modal.querySelector('.lite-video--vimeo iframe');
          const command = {
            method: 'pause',
            value: 'true',
          };
          if (iframe) {
            iframe.contentWindow.postMessage(JSON.stringify(command), '*');
          }
        },
      });
    }
  }

  closeModal() {
    // Only close the modal if it's open in case it's programmatically triggered multiple times.
    if (this.isModalOpen()) {
      MicroModal.close(this.microModalTargetId);
    }
  }

  hasModalUrlHash() {
    return window.location.hash === this.modalIdWithLeadingHash;
  }

  modalHasExistingCookie() {
    // Return if cookie modal is enabled and there is an existing cookie
    return this.areCookiesEnabled() && Cookies.get(this.modalId) === 'disabled';
  }

  setModalCookieIfUnset() {
    // Get the cookie for this modal, if any.
    const cookiedModal = Cookies.get(this.modalId);

    // All cookies are constrained to the current path of the page see js-cookie docs for info. Cookies default to session in this case which means it expires when the page closes.
    const settings = {path: ''};
    if (this.cookieDelayDays) {
      // If there's a cookie delay then add an expiry this changes the mode from session to expire after a certain number of days.
      settings['expires'] = this.cookieDelayDays;
    }
    if (cookiedModal !== 'disabled') {
      // If this cookie is not set then set it. It's set to the string 'disabled' to for readability.
      Cookies.set(this.modalId, 'disabled', settings);
    }
  }

  enableGate(gatedBy) {
    const gatedById = gatedBy.replace('#', '');
    // Find the modal that matches the gatedBy ID and show it first
    const gateModal = document.querySelector(`[data-modal-id="${gatedById}"]`);
    if (
      gateModal &&
      'mountedModules' in window &&
      Array.isArray(window.mountedModules)
    ) {
      // Find the corresponding Modal instance
      const gateModalInstance = window.mountedModules.find(
        (module) => module instanceof Modal && module.modalId === gatedById,
      );
      if (gateModalInstance && gateModalInstance.modalIsReady) {
        gateModalInstance.showModal();
        gateModalInstance.onModalFormSubmit(() => {
          // When the form is submitted show the gated modal,
          // Remove the data-gated-by attribute to stop future gating
          if (this.element.hasAttribute('data-gated-by')) {
            gateModalInstance.closeModal();
            this.updateMarketo(
              this.element.getAttribute('data-marketo-identifier'),
            );
            this.showModal();
          }
        });
      }
    }
  }

  hijackLinksToShowModal() {
    // If an empty string is passed as the trigger ID (which isn't a valid trigger) this will target elements with just # which picks up the wrong links. Make sure there's at least two characters to prevent the selector below from targeting the incorrect links.
    if (this.modalIdWithLeadingHash.length <= 1) {
      return;
    }
    const escapeAttr = (value) => {
      if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(value);
      }
      return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    };
    const modalHref = this.modalIdWithLeadingHash;
    const fragmentSuffix = modalHref;
    const seen = new Set();
    const bindHijack = (element) => {
      if (seen.has(element)) {
        return;
      }
      if (element.closest('.searchModal')) {
        return;
      }
      seen.add(element);
      element.addEventListener('click', (event) => {
        const gatedBy = this.element.getAttribute('data-gated-by');
        if (gatedBy) {
          event.preventDefault();
          this.enableGate(gatedBy);
        } else {
          this.showModal();
          event.preventDefault();
        }
      });
    };
    // Exact hash match (e.g. href="#id") plus hub/absolute URLs ending with the same fragment (e.g. /training/resources/#id).
    document
      .querySelectorAll(`a[href="${escapeAttr(modalHref)}"]`)
      .forEach(bindHijack);
    document
      .querySelectorAll(`a[href$="${escapeAttr(fragmentSuffix)}"]`)
      .forEach(bindHijack);
  }

  listenForExitIntent() {
    // If the modal isn't cookied then set an event listener to open it when the user mouses out of the page
    if (!this.modalHasExistingCookie()) {
      document.addEventListener('mouseout', (e) => {
        if (!e.relatedTarget && !this.exitIntentTriggerFired) {
          this.showModal();

          // Prevent the modal from being continuously fired when the exit intent conditions are met (otherwise modal will appear everytime the user scrolls out of the page)
          this.exitIntentTriggerFired = true;

          this.setModalCookieIfUnset();
        }
      });
    }
  }

  listenForPercentOfPageViewedTrigger() {
    document.addEventListener('scroll', () => {
      // Calculate what percentage of the page is scrolled
      const percent =
        ((document.documentElement.scrollTop || document.body.scrollTop) /
          ((document.documentElement.scrollHeight ||
            document.body.scrollHeight) -
            document.documentElement.clientHeight)) *
        100;
      if (
        percent >= this.triggerAfterPercentOfPageViewed &&
        !this.percentOfPageViewTriggerHasFired
      ) {
        this.showModal();

        // Prevent the modal from being continuously fired once the user passes the trigger percentage
        this.percentOfPageViewTriggerHasFired = true;

        // Set a cookie to prevent the modal from retriggering if the modal is in a cookie-enabled mode
        this.setModalCookieIfUnset();
      }
    });
  }

  disableGating() {
    this.trackUngatedLinks();
    document.querySelectorAll('[data-gated-by]').forEach((element) => {
      element.removeAttribute('data-gated-by');
    });
  }

  initialize() {
    this.bindUI();
    this.setVars();

    if ('evModalsRegistered' in window === false) {
      window.evModalsRegistered = {};
    }

    if (window.evModalsRegistered) {
      if (this.microModalTargetId in window.evModalsRegistered) {
        // Check for modals with invalid duplicate trigger IDs, and log an error if one is found
        const thisValue = this;
        window.addEventListener('load', function() {
          console.error(
            `Duplicate Modal Error: Modal with ID ${thisValue.microModalTargetId} is already registered. Please ensure that each modal has a unique ID.`,
          );
        });
      } else {
        window.evModalsRegistered[this.microModalTargetId] = true;
      }
    }

    // The 'this' value is messed up within event listeners and the class method so we bind the correct value so that this.canDoThis()
    const hasModalUrlHash = this.hasModalUrlHash.bind(this);
    const showModal = this.showModal.bind(this);
    const hijackLinksToShowModal = this.hijackLinksToShowModal.bind(this);

    // Even though this requires the DOM to be ready _src/static/js/lib/register-module.js waits until the DOM is loaded to there's no need to add a listener for that.
    hijackLinksToShowModal();

    if (this.triggerOnExitIntent) {
      // Delay before activating the exit intent - designed to protect modals from opening if users scrolling in/out of the page when first navigating to it
      setTimeout(
        () => this.listenForExitIntent(),
        this.exitIntentActivationDelay,
      );
    }

    if (this.triggerAfterPercentOfPageViewed) {
      this.listenForPercentOfPageViewedTrigger();
    }

    if (this.enableUrlTrigger) {
      window.addEventListener('load', () => {
        // Show modal if the #trigger is present on page load
        if (hasModalUrlHash()) {
          const gatedBy = this.element.getAttribute('data-gated-by');
          if (gatedBy) {
            this.enableGate(gatedBy);
          } else {
            this.updateMarketo(this.mktoIdentifier);
            showModal();
          }
        }
      });
      window.addEventListener('hashchange', () => {
        // Show modal when the #trigger is added or removed
        if (hasModalUrlHash()) {
          const gatedBy = this.element.getAttribute('data-gated-by');
          if (gatedBy) {
            this.enableGate(gatedBy);
          } else {
            this.updateMarketo(this.mktoIdentifier);
            showModal();
          }
        }
      });
    }

    if (this.gateModal) {
      this.callbackQueue = [];
      // If the gate modal cookie is set then remove the data-gated-by attributes
      // associated with this modal
      if (Cookies.get('gatec-' + this.modalId) === 'off') {
        this.disableGating();
      }

      this.gateLinks();

      const searchModalResults = document.querySelectorAll('.searchModal__results-list');

      if (searchModalResults[0]) {
        const observerConfig = {childList: true};
        const thisScope = this;

        const callback = function(mutationsList) {
          for (const mutation of mutationsList) {
            if (mutation.type === 'childList') {
              if (mutation.addedNodes.length > 0) {
                if (Cookies.get('gatec-' + thisScope.modalId) === 'off') {
                  mutation.addedNodes.forEach((node) => {
                    if (node.nodeType !== Node.ELEMENT_NODE) {
                      return;
                    }
                    thisScope.trackUngatedLinks(node);
                  });
                } else {
                  mutation.addedNodes.forEach((node) => {
                    if (node.nodeType !== Node.ELEMENT_NODE) {
                      return;
                    }
                    const aTag = node.matches('a') ?
                      node :
                      node.querySelector('a');
                    const href = aTag?.getAttribute('href');
                    const hitType = aTag?.getAttribute('data-search-hit-type');
                    if (
                      aTag &&
                      href &&
                      !href.startsWith('#') &&
                      hitType !== 'trainingWorkflow'
                    ) {
                      aTag.setAttribute('data-gated-by', thisScope.modalIdWithLeadingHash);
                    }
                  });
                  thisScope.gateLinks(mutation.target);
                }
              }
            }
          }
        };

        const observer = new MutationObserver(callback);
        observer.observe(searchModalResults[0], observerConfig);
      }

      document.addEventListener(
        'marketoFormSubmitEvent',
        (event) => {
          if (
            event.detail.formInstance.formId ===
            this.element
              .querySelector('[data-form-id]')
              .getAttribute('data-form-id')
          ) {
            const noFollowUpAction = this.callbackQueue.length === 0;
            // Execute all callbacks in the queue in reverse order
            for (let i = this.callbackQueue.length - 1; i >= 0; i--) {
              if (typeof this.callbackQueue[i] === 'function') {
                this.callbackQueue[i]();
              }
              if (i === this.callbackQueue.length - 1) {
                // Disable gating after the first callback is executed,
                // the first callback shows the proper resource
                // subsequent callbacks are ignored since they
                // will lack data-gated-by attributes
                this.disableGating();
              }
            }
            this.callbackQueue = [];
            Cookies.set('gatec-' + this.modalId, 'off', {expires: 180});
            if (noFollowUpAction) {
              this.disableGating();
              this.closeModal();
            }
          }
        },
        {once: true},
      );

      this.onModalFormSubmit = (callback) => {
        this.callbackQueue.push(callback);
      };
    }
  }

  areCookiesEnabled() {
    // Check if one of the two cookie modes is enabled
    return this.enableCookieSessionMode || this.enableCookieDelayRetriggerMode;
  }

  getCookieDelayDays() {
    // Parses the data attribute and returns the number of days (necessary because data attributes are strings).
    const cookieDelayDays = parseInt(
      this.element.dataset.enableCookieDelayRetriggerDays,
    );
    if (!isNaN(cookieDelayDays) && cookieDelayDays > 0) {
      return cookieDelayDays;
    }
  }

  getExitIntentActivationDelay() {
    const exitIntentActivationDelaySeconds = parseFloat(
      this.element.dataset.exitIntentActivationDelay,
    );
    if (isNaN(exitIntentActivationDelaySeconds)) {
      return this.defaultExitIntentActivationDelay;
    }
    return exitIntentActivationDelaySeconds * 1000;
  }

  getTriggerAfterPercentOfPageViewed() {
    // Convert the data attribute to a number containing the % of page viewed.
    const triggerAfterPercentOfPageViewed = parseInt(
      this.element.dataset.triggerAfterPercentOfPageViewed,
    );
    if (!isNaN(triggerAfterPercentOfPageViewed)) {
      return triggerAfterPercentOfPageViewed;
    }
  }

  updateMarketo(mktoIdentifier) {
    if (!mktoIdentifier) return;
    if (window.Munchkin) {
      // If Munchkin is available then send the visitWebPage event
      const domain = location.protocol + '//' + location.host;
      const data = {
        url: `${location.href.substring(domain.length)}`,
        params: `training-resource-event-track=${encodeURIComponent(mktoIdentifier)}&url=${encodeURIComponent(location.href)}`,
      };
      window.Munchkin.munchkinFunction('visitWebPage', data);
      window.DD_LOGS && window.DD_LOGS.onReady(() => window.DD_LOGS.logger.info('marketo.visitWebPage', data));
    } else {
      // If Munchkin is not available then wait for it to be available
      this.waitForMunchkin(
        {interval: 100, maxTries: 50},
        () => {
          this.updateMarketo(mktoIdentifier);
        },
        () => {
          console.error('Munchkin not available.');
        },
      );
    }
  }

  waitForMunchkin(settings, onSuccess, onError) {
    if (window.Munchkin) {
      onSuccess();
    } else if (settings.maxTries - 1 <= 0) {
      onError();
    } else {
      setTimeout(() => {
        this.waitForMunchkin(
          {interval: settings.interval, maxTries: settings.maxTries - 1},
          onSuccess,
          onError,
        );
      }, settings.interval);
    }
  }

  setVars() {
    // Define variables
    this.modalId = this.element.dataset.modalId;
    this.enableUrlTrigger = this.element.hasAttribute(
      'data-enable-url-trigger',
    );
    this.enableCookieSessionMode = this.element.hasAttribute(
      'data-enable-cookie-session',
    );
    this.enableCookieDelayRetriggerMode = this.element.hasAttribute(
      'data-enable-cookie-delay-retrigger-days',
    );
    this.onModalOpen = null;
    this.onModalClose = null;
    this.microModalTargetId = `${this.modalId}-id`;
    this.cookieDelayDays = this.getCookieDelayDays();
    this.modalIdWithLeadingHash = `#${this.modalId}`;
    this.openClassName = 'is-open';
    this.triggerOnExitIntent = this.element.hasAttribute(
      'data-trigger-on-exit-intent',
    );
    this.triggerAfterPercentOfPageViewed =
      this.getTriggerAfterPercentOfPageViewed();
    this.defaultExitIntentActivationDelay = 3000;
    this.exitIntentActivationDelay = this.getExitIntentActivationDelay();
    this.gateModal = this.element.hasAttribute('data-gate-modal');
    this.mktoIdentifier = this.element.dataset.marketoIdentifier || null;
  }

  gateLinks(scope = document) {
    // Get all links on the page
    // Only select links that have a data-gated-by attribute matching this modal's ID

    const links = scope.querySelectorAll('a[data-gated-by]');

    links.forEach((link) => {
      // Skip links that don't have data-gated-by attribute matching this modal's ID
      // const gatedByValue = `${this.modalId}`;
      const listener = (event) => {
        const linkElement = event.target.closest('a');
        if (linkElement.getAttribute('data-gated-by')) {
          event.preventDefault();
          this.showModal();
          this.onModalFormSubmit(() => {
            if (linkElement.getAttribute('data-gated-by')) {
              // Check for data-gated-by on link before opening the window
              // to ensure the window is only opened once
              this.closeModal();
              const url = linkElement.getAttribute('href');
              const target = linkElement.getAttribute('target');
              // Open in new window if target is "_blank", otherwise navigate in same window
              this.updateMarketo(
                linkElement.getAttribute('data-marketo-identifier'),
              );
              if (target === '_blank') {
                window.open(url, '_blank');
              } else {
                window.location.href = url;
              }
            }
            // This is outside the conditional because
            // whenever the modal form submission is
            // successful all links associated with
            // that form need listeners removed
            link.removeEventListener('click', listener);
          });
        }
      };
      link.addEventListener('click', listener);
    });
  }

  trackUngatedLinks(scope = document) {
    // Get all links on the page which have a data-marketo-identifier attribute
    // Add a listener on each link to send a visitWebPage event to Marketo when the link is clicked (only applied when the gate is passed)
    const links = scope.querySelectorAll('a[data-marketo-identifier]');

    links.forEach((link) => {
      if (link.classList.contains('searchResult')) {
        if (link.getAttribute('href') && link.getAttribute('href').startsWith('#')) {
          return;
        }
      }
      link.addEventListener('click', (event) => {
        const linkElement = event.target.closest('a');
        if (linkElement.getAttribute('data-marketo-identifier')) {
          const mktoIdentifier = linkElement.getAttribute(
            'data-marketo-identifier',
          );
          this.updateMarketo(mktoIdentifier);
        }
        if (!linkElement) {
          const linkElements = event.target.querySelectorAll('a');
          linkElements.forEach((linkElement) => {
            if (linkElement.getAttribute('data-marketo-identifier')) {
              const mktoIdentifier = linkElement.getAttribute(
                'data-marketo-identifier',
              );
              this.updateMarketo(mktoIdentifier);
            }
          });
        }
      });
    });
  }
}

registerModule('Modal', Modal);
